import React, { useState, useCallback, useEffect, useRef } from "react";
import { ThemeProvider } from "@mui/material/styles";
import axios from "axios";
import {
  Grid,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  MenuItem,
  Tooltip,
  Card,
} from "@mui/material";
import {
  StyledTableCellHistory,
  StyledTableRowHistory,
  StyledTextField,
  StyledButton,
  OutlinedFab,
  theme,
  StyledMultilineTextField,
  OutlinedIconFab,
  StyledTableContainer,
} from "../StyledComponents";
import Suggestions from "../Utils/Suggestions";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import MicOffIcon from "@mui/icons-material/MicOff";
import ClearIcon from "@mui/icons-material/Clear";
import { toast } from "react-toastify";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import Icon from "../Utils/Icons";
import { InvestigationsTable } from "./InvestigationsTable";

const Investigations = ({
  sectionName,
  activeSection,
  suggestions,
  aiSuggestionToggle,
  ruleSuggestionToggle,
  handleCrossButtonClick,
  data,
  setData,
  startRecording,
  stopRecording,
  isAiEnabled,
}) => {
  console.log("Inside Tests and Investigations", data);

  const [authToken, setAuthToken] = useState(
    sessionStorage.getItem("accessToken") !== undefined &&
      sessionStorage.getItem("accessToken") !== null
      ? sessionStorage.getItem("accessToken")
      : ""
  );
  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const headers = {
    Authorization: `Bearer ${authToken}`,
  };
  const [autocompleteData, setAutoCompleteData] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [filteredBodyPartsOptions, setFilteredBodyPartsOptions] = useState([]);
  const [filteredLateralityOptions, setFilteredLateralityOptions] = useState(
    []
  );
  const [filteredPositionOptions, setFilteredPositionOptions] = useState([]);
  const [suggestApiCalled, setSuggestApiCalled] = useState(false);
  const [debounceFunctionCalled, setDebounceFunctionCalled] = useState(false);
  const [
    debounceFunctionCalledForBodyParts,
    setDebounceFunctionCalledForBodyParts,
  ] = useState(false);
  const [
    debounceFunctionCalledForLaterality,
    setDebounceFunctionCalledForLaterality,
  ] = useState(false);
  const [
    debounceFunctionCalledForPosition,
    setDebounceFunctionCalledForPosition,
  ] = useState(false);
  const { transcript, resetTranscript } = useSpeechRecognition();
  const [listeningOn, setListeningOn] = useState(false);
  const [newInvestigation, setNewInvestigation] = useState({
    term: "",
    id: null,
    clinical_note: "",
    body_part: "",
    body_part_id: null,
    laterality: "",
    laterality_id: null,
    position: "",
    position_id: null,
  });
  const [termSelectedOrAdded, setTermSelectedOrAdded] = useState(false);
  const [bodyPartSelectedOrAdded, setBodyPartSelectedOrAdded] = useState(false);
  const [lateralitySelectedOrAdded, setLateralitySelectedOrAdded] =
    useState(false);
  const [positionSelectedOrAdded, setPositionSelectedOrAdded] = useState(false);
  const [investigationName, setInvestigationName] = useState("");
  const [bodyPartName, setBodyPartName] = useState("");
  const [lateralityName, setLateralityName] = useState("");
  const [positionName, setPositionName] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [investigations, setInvestigations] = useState(data);
  const [validationErrors, setValidationErrors] = useState({
    term: "",
  });
  const [isFocused, setIsFocused] = useState(false);
  const [mouseDownOnOption, setMouseDownOnOption] = useState(false);
  const [mouseDownOnBodyPartOption, setMouseDownOnBodyPartOption] =
    useState(false);
  const [mouseDownOnLateralityOption, setMouseDownOnLateralityOption] =
    useState(false);
  const [mouseDownOnPositionOption, setMouseDownOnPositionOption] =
    useState(false);
  const [suggestionClicked, setSuggestionClicked] = useState(false);

  const debounceTimeout = useRef(null); // Use useRef to persist timeout
  const skipEffectForTest = useRef(false);
  const skipEffectForBodyPart = useRef(false);
  const skipEffectForLaterality = useRef(false);
  const skipEffectForPosition = useRef(false);
  const testChosen = useRef(false);
  const bodyPartChosen = useRef(false);
  const lateralityChosen = useRef(false);
  const positionChosen = useRef(false);

  useEffect(() => {
    setInvestigations(data);
  }, [data]);

  useEffect(() => {
    setAuthToken(sessionStorage.getItem("accessToken"));
  }, [sessionStorage.getItem("accessToken")]);

  useEffect(() => {
    // alert(`DH ${activeHistorySection}`);
    if (activeSection) {
      let timerId;

      timerId = setTimeout(() => {
        setInvestigationName(transcript);
      }, 3000);

      return () => {
        clearTimeout(timerId);
      };
    }
  }, [transcript]);

  useEffect(() => {
    if (skipEffectForTest.current) {
      skipEffectForTest.current = false; // reset after skipping
      return;
    }

    // Call the API with the debounced function when investigationName changes

    let timerOut = setTimeout(() => {
      if (investigationName.trim() !== "") {
        debouncedFilterCall(investigationName.toLowerCase());
      }
    }, 1000);
    return () => clearInterval(timerOut);
  }, [investigationName]);

  useEffect(() => {
    if (skipEffectForBodyPart.current) {
      skipEffectForBodyPart.current = false; // reset after skipping
      return;
    }

    // Call the API with the debounced function when bodyPartName changes

    let timerOut = setTimeout(() => {
      if (bodyPartName.trim() !== "") {
        debouncedFilterCallForBodyParts(bodyPartName.toLowerCase());
      }
    }, 1000);
    return () => clearInterval(timerOut);
  }, [bodyPartName]);

  useEffect(() => {
    if (skipEffectForLaterality.current) {
      skipEffectForLaterality.current = false; // reset after skipping
      return;
    }

    // Call the API with the debounced function when lateralityName changes

    let timerOut = setTimeout(() => {
      if (lateralityName.trim() !== "") {
        debouncedFilterCallForLaterality(lateralityName.toLowerCase());
      }
    }, 1000);
    return () => clearInterval(timerOut);
  }, [lateralityName]);

  useEffect(() => {
    if (skipEffectForPosition.current) {
      skipEffectForPosition.current = false; // reset after skipping
      return;
    }

    // Call the API with the debounced function when positionName changes

    let timerOut = setTimeout(() => {
      if (positionName.trim() !== "") {
        debouncedFilterCallForPosition(positionName.toLowerCase());
      }
    }, 1000);
    return () => clearInterval(timerOut);
  }, [positionName]);

  const startListening = (key) => {
    // alert("MH SL");
    if (transcript) {
      resetTranscript();
    }
    // setActiveHistorySection("Family History");
    SpeechRecognition.startListening({ continuous: true, language: "en-IN" });
    setListeningOn(true);
  };
  const debouncedFilterCall = async (name) => {
    console.log("Debounce function called");
    let semanticTagForInvestigations = "procedure";
    const postBody = {
      term: name,
      state: "active",
      semantictag: semanticTagForInvestigations,
      acceptability: "synonyms",
      returnlimit: "-1",
      refsetid: null,
      parentid: null,
      excludeparentconcept: false,
      fullconcept: true,
    };

    try {
      if (investigationName.length > 2) {
        const response = await axios.post(
          `${REACT_APP_BACKEND_ROUTE_URL}snomed/search/`,
          postBody,
          { headers: headers }
        );
        if (response) {
          console.log("Snomed Response", response);
          if (
            response.data.status === "Success" &&
            response.data.data.length > 0
          ) {
            const dataToProcess =
              response.data.data.length > 300
                ? response.data.data.slice(0, 300)
                : response.data.data;

            const filteredData = dataToProcess.map((item) => {
              return {
                term: item.term,
                id: item.conceptId,
              };
            });
            console.log("Filtered Medicine Data", filteredData);
            setAutoCompleteData(filteredData);
            setFilteredOptions(filteredData);
          } else {
            try {
              if (investigationName.length > 2) {
                const response = await axios.post(
                  `${REACT_APP_BACKEND_ROUTE_URL}snomed/suggest/`,
                  postBody,
                  { headers: headers }
                );
                if (response) {
                  console.log("Suggest Response", response);
                  if (
                    response.data.status === "Success" &&
                    response.data.data.length > 0
                  ) {
                    setSuggestApiCalled(true);
                    const dataToProcess =
                      response.data.data.length > 300
                        ? response.data.data.slice(0, 300)
                        : response.data.data;
                    setAutoCompleteData(dataToProcess);
                    setFilteredOptions(dataToProcess);
                    // setSelectedFromSuggestion(true);
                  } else {
                    // setSelectedFromSuggestion(true);

                    setAutoCompleteData([]);
                    setFilteredOptions([]);
                  }
                }
              }
            } catch (err) {
              console.log(err);
              toast.error(err);
            }
          }
          setDebounceFunctionCalled(true);
        }
      }
    } catch (err) {
      console.log(err);
      toast.error(err);
    }
  };
  const debouncedFilterCallForBodyParts = async (name) => {
    console.log("Debounce function called");
    let semanticTagForInvestigations = "body structure";
    const postBody = {
      term: name,
      state: "active",
      semantictag: semanticTagForInvestigations,
      acceptability: "synonyms",
      returnlimit: "-1",
      refsetid: null,
      parentid: null,
      excludeparentconcept: false,
      fullconcept: true,
    };

    try {
      if (bodyPartName.length > 2) {
        const response = await axios.post(
          `${REACT_APP_BACKEND_ROUTE_URL}snomed/search/`,
          postBody,
          { headers: headers }
        );
        if (response) {
          console.log("Snomed Response", response);
          if (
            response.data.status === "Success" &&
            response.data.data.length > 0
          ) {
            const dataToProcess =
              response.data.data.length > 300
                ? response.data.data.slice(0, 300)
                : response.data.data;

            const filteredData = dataToProcess.map((item) => {
              return {
                term: item.term,
                id: item.conceptId,
              };
            });
            console.log("Filtered Medicine Data", filteredData);
            setAutoCompleteData(filteredData);
            setFilteredBodyPartsOptions(filteredData);
          } else {
            try {
              if (bodyPartName.length > 2) {
                const response = await axios.post(
                  `${REACT_APP_BACKEND_ROUTE_URL}snomed/suggest/`,
                  postBody,
                  { headers: headers }
                );
                if (response) {
                  console.log("Suggest Response", response);
                  if (
                    response.data.status === "Success" &&
                    response.data.data.length > 0
                  ) {
                    setSuggestApiCalled(true);
                    const dataToProcess =
                      response.data.data.length > 300
                        ? response.data.data.slice(0, 300)
                        : response.data.data;
                    setAutoCompleteData(dataToProcess);
                    setFilteredBodyPartsOptions(dataToProcess);
                    // setSelectedFromSuggestion(true);
                  } else {
                    // setSelectedFromSuggestion(true);

                    setAutoCompleteData([]);
                    setFilteredBodyPartsOptions([]);
                  }
                }
              }
            } catch (err) {
              console.log(err);
              toast.error(err);
            }
          }
          setDebounceFunctionCalledForBodyParts(true);
        }
      }
    } catch (err) {
      console.log(err);
      toast.error(err);
    }
  };
  const debouncedFilterCallForLaterality = async (name) => {
    console.log("Debounce function called");
    let semanticTagForInvestigations = "qualifier value";
    const postBody = {
      term: name,
      state: "active",
      semantictag: semanticTagForInvestigations,
      acceptability: "synonyms",
      returnlimit: "-1",
      refsetid: null,
      parentid: null,
      excludeparentconcept: false,
      fullconcept: true,
    };

    try {
      if (lateralityName.length > 2) {
        const response = await axios.post(
          `${REACT_APP_BACKEND_ROUTE_URL}snomed/search/`,
          postBody,
          { headers: headers }
        );
        if (response) {
          console.log("Snomed Response", response);
          if (
            response.data.status === "Success" &&
            response.data.data.length > 0
          ) {
            const dataToProcess =
              response.data.data.length > 300
                ? response.data.data.slice(0, 300)
                : response.data.data;

            const filteredData = dataToProcess.map((item) => {
              return {
                term: item.term,
                id: item.conceptId,
              };
            });
            console.log("Filtered Medicine Data", filteredData);
            setAutoCompleteData(filteredData);
            setFilteredLateralityOptions(filteredData);
          } else {
            try {
              if (lateralityName.length > 2) {
                const response = await axios.post(
                  `${REACT_APP_BACKEND_ROUTE_URL}snomed/suggest/`,
                  postBody,
                  { headers: headers }
                );
                if (response) {
                  console.log("Suggest Response", response);
                  if (
                    response.data.status === "Success" &&
                    response.data.data.length > 0
                  ) {
                    setSuggestApiCalled(true);
                    const dataToProcess =
                      response.data.data.length > 300
                        ? response.data.data.slice(0, 300)
                        : response.data.data;
                    setAutoCompleteData(dataToProcess);
                    setFilteredLateralityOptions(dataToProcess);
                    // setSelectedFromSuggestion(true);
                  } else {
                    // setSelectedFromSuggestion(true);

                    setAutoCompleteData([]);
                    setFilteredLateralityOptions([]);
                  }
                }
              }
            } catch (err) {
              console.log(err);
              toast.error(err);
            }
          }
          setDebounceFunctionCalledForLaterality(true);
        }
      }
    } catch (err) {
      console.log(err);
      toast.error(err);
    }
  };
  const debouncedFilterCallForPosition = async (name) => {
    console.log("Debounce function called");
    let semanticTagForInvestigations = "finding";
    const postBody = {
      term: name,
      state: "active",
      semantictag: semanticTagForInvestigations,
      acceptability: "synonyms",
      returnlimit: "-1",
      refsetid: null,
      parentid: null,
      excludeparentconcept: false,
      fullconcept: true,
    };

    try {
      if (positionName.length > 2) {
        const response = await axios.post(
          `${REACT_APP_BACKEND_ROUTE_URL}snomed/search/`,
          postBody,
          { headers: headers }
        );
        if (response) {
          console.log("Snomed Response", response);
          if (
            response.data.status === "Success" &&
            response.data.data.length > 0
          ) {
            const dataToProcess =
              response.data.data.length > 300
                ? response.data.data.slice(0, 300)
                : response.data.data;

            const filteredData = dataToProcess.map((item) => {
              return {
                term: item.term,
                id: item.conceptId,
              };
            });
            console.log("Filtered Medicine Data", filteredData);
            setAutoCompleteData(filteredData);
            setFilteredPositionOptions(filteredData);
          } else {
            try {
              if (positionName.length > 2) {
                const response = await axios.post(
                  `${REACT_APP_BACKEND_ROUTE_URL}snomed/suggest/`,
                  postBody,
                  { headers: headers }
                );
                if (response) {
                  console.log("Suggest Response", response);
                  if (
                    response.data.status === "Success" &&
                    response.data.data.length > 0
                  ) {
                    setSuggestApiCalled(true);
                    const dataToProcess =
                      response.data.data.length > 300
                        ? response.data.data.slice(0, 300)
                        : response.data.data;
                    setAutoCompleteData(dataToProcess);
                    setFilteredPositionOptions(dataToProcess);
                    // setSelectedFromSuggestion(true);
                  } else {
                    // setSelectedFromSuggestion(true);

                    setAutoCompleteData([]);
                    setFilteredPositionOptions([]);
                  }
                }
              }
            } catch (err) {
              console.log(err);
              toast.error(err);
            }
          }
          setDebounceFunctionCalledForPosition(true);
        }
      }
    } catch (err) {
      console.log(err);
      toast.error(err);
    }
  };
  const validateFields = () => {
    const errors = {};

    if (!newInvestigation.term && investigationName.length === 0) {
      errors.term = "Tests/Investigations Name is a mandatory field.";
    }

    setValidationErrors(errors);

    return Object.keys(errors).length === 0; // Return true if no errors
  };
  // Function to handle adding a new row
  // const handleAdd = async () => {
  //   if (!validateFields()) {
  //     return; // Exit if validation fails
  //   }

  //   let newDetails,
  //     updatedFound = {},
  //     updatedSelectedOptiondetsils = {},
  //     foundData = false;

  //   if (!testChosen.current) {
  //     const postBody = {
  //       term: investigationName,
  //       state: "active",
  //       semantictag: "procedure",
  //       acceptability: "synonyms",
  //       returnlimit: "-1",
  //       refsetid: null,
  //       parentid: null,
  //       excludeparentconcept: false,
  //       fullconcept: true,
  //     };

  //     try {
  //       if (investigationName.length > 2) {
  //         const response = await axios.post(
  //           `${REACT_APP_BACKEND_ROUTE_URL}snomed/search/`,
  //           postBody,
  //           { headers: headers }
  //         );
  //         if (response) {
  //           console.log("Snomed Response", response);
  //           if (
  //             response.data.status === "Success" &&
  //             response.data.data.length > 0
  //           ) {
  //             foundData = response.data.data.find((d) => {
  //               console.log(d.term);
  //               console.log(investigationName);
  //               console.log(d.term === investigationName);
  //               return d.term === investigationName;
  //             });
  //             console.log("Found Data", foundData);
  //             if (foundData) {
  //               updatedFound = {
  //                 ...foundData,
  //                 found: true,
  //                 id: foundData.conceptId,
  //               };
  //             } else {
  //               updatedSelectedOptiondetsils = {
  //                 hierarchy: null,
  //                 isPreferredTerm: null,
  //                 conceptState: null,
  //                 conceptFsn: investigationName,
  //                 definitionStatus: null,
  //                 conceptId: null,
  //                 languageCode: "en",
  //                 typeId: null,
  //                 term: investigationName,
  //                 caseSignificanceId: "CASE_INSENSITIVE",
  //                 id: null,
  //                 effectiveTime: null,
  //                 activeStatus: null,
  //                 moduleId: null,
  //               };
  //             }
  //           } else {
  //             updatedSelectedOptiondetsils = {
  //               hierarchy: null,
  //               isPreferredTerm: null,
  //               conceptState: null,
  //               conceptFsn: investigationName,
  //               definitionStatus: null,
  //               conceptId: null,
  //               languageCode: "en",
  //               typeId: null,
  //               term: investigationName,
  //               caseSignificanceId: "CASE_INSENSITIVE",
  //               id: null,
  //               effectiveTime: null,
  //               activeStatus: null,
  //               moduleId: null,
  //             };
  //           }

  //           if (foundData) {
  //             console.log("updatedFound", updatedFound);
  //             newDetails = {
  //               ...newInvestigation,
  //               term: updatedFound.term,
  //               id: updatedFound.id,
  //             };
  //           } else {
  //             console.log(
  //               "updatedSelectedOptiondetsils",
  //               updatedSelectedOptiondetsils
  //             );
  //             newDetails = {
  //               ...newInvestigation,
  //               term: updatedSelectedOptiondetsils.term,
  //               id: updatedSelectedOptiondetsils.id,
  //             };
  //           }
  //         } else {
  //           newDetails = {
  //             ...newInvestigation,
  //             term: investigationName,
  //             id: null,
  //           };
  //           // handleInputChange("condition", updatedSelectedOptiondetsils);
  //         }
  //       }
  //     } catch (err) {
  //       console.log(err);
  //       // toast.error(err);
  //       newDetails = {
  //         ...newInvestigation,
  //         term: investigationName,
  //         id: null,
  //       };
  //     }
  //   } else {
  //     newDetails = { ...newInvestigation };
  //   }

  //   // console.log("New Investigation Added", newDetails);
  //   // console.log("New Investigation Added2", newInvestigation);

  //   // Add the new term to the complaints list
  //   // let updatedDetails = [...investigations, newInvestigation];
  //   let updatedDetails = [...investigations, newDetails];
  //   console.log("Investigation Updated Details", updatedDetails);
  //   setInvestigations(updatedDetails);
  //   setNewInvestigation({
  //     term: "",
  //     id: null,
  //     clinical_note: "",
  //   });
  //   setValidationErrors({}); // Clear errors after successful submission
  //   setDebounceFunctionCalled(false);
  //   setTermSelectedOrAdded(false);
  //   setInvestigationName("");
  //   setData(updatedDetails);
  //   testChosen.current = false;
  //   setFilteredOptions([]);
  // };
  const handleAdd = async () => {
    if (!validateFields()) return;

    let newDetails = { ...newInvestigation };
    const fallbackDetails = {
      term: investigationName,
      id: null,
    };

    try {
      if (!testChosen.current && investigationName.length > 2) {
        const snomedTest = await getSnomedConcept(
          investigationName,
          headers,
          "procedure"
        );
        newDetails.term = snomedTest.term;
        newDetails.id = snomedTest.id;
      }

      if (!bodyPartChosen.current && bodyPartName.length > 2) {
        const snomedBodyPart = await getSnomedConcept(
          bodyPartName,
          headers,
          "body structure"
        );
        newDetails.body_part = snomedBodyPart.term;
        newDetails.body_part_id = snomedBodyPart.id;
      }

      if (!lateralityChosen.current && lateralityName.length > 2) {
        const snomedLaterality = await getSnomedConcept(
          lateralityName,
          headers,
          "finding"
        );
        newDetails.laterality = snomedLaterality.term;
        newDetails.laterality_id = snomedLaterality.id;
      }

      if (!positionChosen.current && positionName.length > 2) {
        const snomedPosition = await getSnomedConcept(positionName, headers);
        newDetails.position = snomedPosition.term;
        newDetails.position_id = snomedPosition.id;
      }
    } catch (err) {
      console.error("Error in handleAdd:", err);
      newDetails = { ...newDetails, ...fallbackDetails };
    }

    const updatedDetails = [...investigations, newDetails];
    console.log("Investigation Updated Details", updatedDetails);
    setInvestigations(updatedDetails);

    // Reset form states
    setNewInvestigation({
      term: "",
      id: null,
      clinical_note: "",
      body_part: "",
      body_part_id: null,
      laterality: "",
      laterality_id: null,
      position: "",
      position_id: null,
    });
    setValidationErrors({});
    setDebounceFunctionCalled(false);
    setDebounceFunctionCalledForBodyParts(false);
    setDebounceFunctionCalledForLaterality(false);
    setDebounceFunctionCalledForPosition(false);
    setTermSelectedOrAdded(false);
    setBodyPartSelectedOrAdded(false);
    setLateralitySelectedOrAdded(false);
    setPositionSelectedOrAdded(false);
    setInvestigationName("");
    setBodyPartName("");
    setLateralityName("");
    setPositionName("");
    testChosen.current = false;
    bodyPartChosen.current = false;
    lateralityChosen.current = false;
    positionChosen.current = false;
    setFilteredOptions([]);
    setFilteredBodyPartsOptions([]);
    setFilteredLateralityOptions([]);
    setFilteredPositionOptions([]);
    setData(updatedDetails);
  };

  const getSnomedConcept = async (name, headers, semanticTag) => {
    if (name.length <= 2) return null;

    const postBody = {
      term: name,
      state: "active",
      semantictag: semanticTag,
      acceptability: "synonyms",
      returnlimit: "-1",
      refsetid: null,
      parentid: null,
      excludeparentconcept: false,
      fullconcept: true,
    };

    try {
      const response = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}snomed/search/`,
        postBody,
        { headers }
      );

      if (
        response?.data?.status === "Success" &&
        response.data.data.length > 0
      ) {
        const foundData = response.data.data.find((d) => d.term === name);
        return foundData
          ? {
              ...foundData,
              found: true,
              id: foundData.conceptId,
            }
          : {
              term: name,
              id: null,
            };
      }
    } catch (error) {
      console.error("SNOMED fetch error:", error);
    }

    return {
      term: name,
      id: null,
    };
  };

  // Function to handle input changes
  const handleInputChange = (field, value) => {
    let newDetails;
    if (field == "term") {
      setAutoCompleteData([]);
      setFilteredOptions([]);
      setTermSelectedOrAdded(true);
      newDetails = {
        ...newInvestigation,
        [field]: value.term,
        id: value.id,
      };
    } else if (field == "body_part") {
      setAutoCompleteData([]);
      setFilteredBodyPartsOptions([]);
      setBodyPartSelectedOrAdded(true);
      newDetails = {
        ...newInvestigation,
        body_part: value.term,
        body_part_id: value.id,
      };
    } else if (field == "laterality") {
      setAutoCompleteData([]);
      setFilteredLateralityOptions([]);
      setLateralitySelectedOrAdded(true);
      newDetails = {
        ...newInvestigation,
        laterality: value.term,
        laterality_id: value.id,
      };
    } else if (field == "position") {
      setAutoCompleteData([]);
      setFilteredPositionOptions([]);
      setPositionSelectedOrAdded(true);
      newDetails = {
        ...newInvestigation,
        position: value.term,
        position_id: value.id,
      };
    } else {
      newDetails = {
        ...newInvestigation,
        [field]: value,
      };
    }

    setNewInvestigation(newDetails);
  };

  // Function to handle row edit
  const handleEditRow = (index) => {
    setInvestigationName(investigations[index].term);
    setBodyPartName(investigations[index].body_part);
    setLateralityName(investigations[index].laterality);
    setPositionName(investigations[index].position);
    setNewInvestigation({
      ...investigations[index],
      term: "",
      id: null,
      // clinical_note: "",
    });
    setEditingIndex(index);
    // handleDeleteRow(index); // Remove the row from the list temporarily
  };
  console.log("Editing Index", editingIndex);
  // const handleUpdate = async () => {
  //   if (!validateFields()) {
  //     return; // Exit if validation fails
  //   }

  //   let newDetails,
  //     updatedFound = {},
  //     updatedSelectedOptiondetsils = {},
  //     foundData = false;

  //   if (!testChosen.current) {
  //     const postBody = {
  //       term: investigationName,
  //       state: "active",
  //       semantictag: "finding++disorder",
  //       acceptability: "synonyms",
  //       returnlimit: "-1",
  //       refsetid: null,
  //       parentid: null,
  //       excludeparentconcept: false,
  //       fullconcept: true,
  //     };

  //     try {
  //       if (investigationName.length > 2) {
  //         const response = await axios.post(
  //           `${REACT_APP_BACKEND_ROUTE_URL}snomed/search/`,
  //           postBody,
  //           { headers: headers }
  //         );
  //         if (response) {
  //           console.log("Snomed Response", response);
  //           if (
  //             response.data.status === "Success" &&
  //             response.data.data.length > 0
  //           ) {
  //             foundData = response.data.data.find(
  //               (d) => d.term === investigationName
  //             );
  //             console.log("Found Data", foundData);
  //             if (foundData) {
  //               updatedFound = {
  //                 ...foundData,
  //                 found: true,
  //                 id: foundData.conceptId,
  //               };
  //             } else {
  //               updatedSelectedOptiondetsils = {
  //                 hierarchy: null,
  //                 isPreferredTerm: null,
  //                 conceptState: null,
  //                 conceptFsn: investigationName,
  //                 definitionStatus: null,
  //                 conceptId: null,
  //                 languageCode: "en",
  //                 typeId: null,
  //                 term: investigationName,
  //                 caseSignificanceId: "CASE_INSENSITIVE",
  //                 id: null,
  //                 effectiveTime: null,
  //                 activeStatus: null,
  //                 moduleId: null,
  //               };
  //             }
  //           } else {
  //             updatedSelectedOptiondetsils = {
  //               hierarchy: null,
  //               isPreferredTerm: null,
  //               conceptState: null,
  //               conceptFsn: investigationName,
  //               definitionStatus: null,
  //               conceptId: null,
  //               languageCode: "en",
  //               typeId: null,
  //               term: investigationName,
  //               caseSignificanceId: "CASE_INSENSITIVE",
  //               id: null,
  //               effectiveTime: null,
  //               activeStatus: null,
  //               moduleId: null,
  //             };
  //           }

  //           if (foundData) {
  //             console.log("updatedFound", updatedFound);
  //             newDetails = {
  //               ...newInvestigation,
  //               term: updatedFound.term,
  //               id: updatedFound.id,
  //             };
  //           } else {
  //             console.log(
  //               "updatedSelectedOptiondetsils",
  //               updatedSelectedOptiondetsils
  //             );
  //             newDetails = {
  //               ...newInvestigation,
  //               term: updatedSelectedOptiondetsils.term,
  //               id: updatedSelectedOptiondetsils.id,
  //             };
  //           }
  //         } else {
  //           newDetails = {
  //             ...newInvestigation,
  //             term: investigationName,
  //             id: null,
  //           };
  //           // handleInputChange("condition", updatedSelectedOptiondetsils);
  //         }
  //       }
  //     } catch (err) {
  //       console.log(err);
  //       // toast.error(err);
  //       newDetails = {
  //         ...newInvestigation,
  //         term: investigationName,
  //         id: null,
  //       };
  //     }
  //   } else {
  //     newDetails = { ...newInvestigation };
  //   }

  //   let updatedDetails = investigations.map((data, i) =>
  //     i == editingIndex ? newDetails : data
  //   );

  //   setInvestigations(updatedDetails);
  //   setEditingIndex(null);
  //   setNewInvestigation({
  //     ...newInvestigation,
  //     term: "",
  //     id: null,
  //     clinical_note: "",
  //   });
  //   setDebounceFunctionCalled(false);
  //   setTermSelectedOrAdded(false);
  //   setInvestigationName("");
  //   setData(updatedDetails);
  //   setFilteredOptions([]);
  //   testChosen.current = false;
  // };
  // Function to handle row delete
  const handleUpdate = async () => {
    if (!validateFields()) return;

    let newDetails = { ...newInvestigation };
    const fallbackDetails = {
      term: investigationName,
      id: null,
    };

    try {
      if (!testChosen.current && investigationName.length > 2) {
        const snomedTest = await getSnomedConcept(
          investigationName,
          headers,
          "procedure"
        );
        newDetails.term = snomedTest.term;
        newDetails.id = snomedTest.id;
      }

      if (!bodyPartChosen.current && bodyPartName.length > 2) {
        const snomedBodyPart = await getSnomedConcept(
          bodyPartName,
          headers,
          "body structure"
        );
        newDetails.body_part = snomedBodyPart.term;
        newDetails.body_part_id = snomedBodyPart.id;
      }

      if (!lateralityChosen.current && lateralityName.length > 2) {
        const snomedLaterality = await getSnomedConcept(
          lateralityName,
          headers,
          "finding"
        );
        newDetails.laterality = snomedLaterality.term;
        newDetails.laterality_id = snomedLaterality.id;
      }

      if (!positionChosen.current && positionName.length > 2) {
        const snomedPosition = await getSnomedConcept(positionName, headers);
        newDetails.position = snomedPosition.term;
        newDetails.position_id = snomedPosition.id;
      }
    } catch (err) {
      console.error("Error in handleUpdate:", err);
      newDetails = { ...newDetails, ...fallbackDetails };
    }

    newDetails = { ...newDetails, source: undefined };

    const updatedDetails = investigations.map((data, i) =>
      i === editingIndex ? newDetails : data
    );

    setInvestigations(updatedDetails);
    setEditingIndex(null);
    setNewInvestigation({
      term: "",
      id: null,
      clinical_note: "",
      body_part: "",
      body_part_id: null,
      laterality: "",
      laterality_id: null,
      position: "",
      position_id: null,
    });

    // Reset state like handleAdd
    setValidationErrors({});
    setDebounceFunctionCalled(false);
    setDebounceFunctionCalledForBodyParts(false);
    setDebounceFunctionCalledForLaterality(false);
    setDebounceFunctionCalledForPosition(false);
    setTermSelectedOrAdded(false);
    setBodyPartSelectedOrAdded(false);
    setLateralitySelectedOrAdded(false);
    setPositionSelectedOrAdded(false);
    setInvestigationName("");
    setBodyPartName("");
    setLateralityName("");
    setPositionName("");
    testChosen.current = false;
    bodyPartChosen.current = false;
    lateralityChosen.current = false;
    positionChosen.current = false;
    setFilteredOptions([]);
    setFilteredBodyPartsOptions([]);
    setFilteredLateralityOptions([]);
    setFilteredPositionOptions([]);
    setData(updatedDetails);
  };

  const handleDeleteRow = (index) => {
    let updatedDetails = investigations.filter((_, i) => i !== index);
    setInvestigations(updatedDetails);
    setData(updatedDetails);
  };
  const memoizedHandleCrossButtonClick = useCallback(
    (sectionName, suggestionName, type) => {
      handleCrossButtonClick(sectionName, suggestionName, type);
    },
    [handleCrossButtonClick]
  );
  const handleClearName = () => {
    setValidationErrors({});
    setInvestigationName("");
    resetTranscript();
    setNewInvestigation({
      ...newInvestigation,
      id: null,
      term: "", // Use computed property name to set the field dynamically
    });
    setFilteredOptions([]);
    setAutoCompleteData([]);
    setDebounceFunctionCalled(false);
    setTermSelectedOrAdded(false);
    // setMedicineList([])
  };
  const handleClearBodyPartName = () => {
    setValidationErrors({});
    setBodyPartName("");
    resetTranscript();
    setNewInvestigation({
      ...newInvestigation,
      body_part_id: null,
      body_part: "", // Use computed property name to set the field dynamically
    });
    setFilteredBodyPartsOptions([]);
    setAutoCompleteData([]);
    setDebounceFunctionCalledForBodyParts(false);
    setBodyPartSelectedOrAdded(false);
    // setMedicineList([])
  };
  const handleClearLateralityName = () => {
    setValidationErrors({});
    setLateralityName("");
    resetTranscript();
    setNewInvestigation({
      ...newInvestigation,
      laterality_id: null,
      laterality: "", // Use computed property name to set the field dynamically
    });
    setFilteredLateralityOptions([]);
    setAutoCompleteData([]);
    setDebounceFunctionCalledForLaterality(false);
    setLateralitySelectedOrAdded(false);
    // setMedicineList([])
  };
  const handleClearPositionName = () => {
    setValidationErrors({});
    setPositionName("");
    resetTranscript();
    setNewInvestigation({
      ...newInvestigation,
      position_id: null,
      position: "", // Use computed property name to set the field dynamically
    });
    setFilteredPositionOptions([]);
    setAutoCompleteData([]);
    setDebounceFunctionCalledForPosition(false);
    setPositionSelectedOrAdded(false);
    // setMedicineList([])
  };
  const handleAddOption = async (term) => {
    if (term.trim().length == 0) {
      const errors = {};
      errors.term = "You can't add an empty string";
      setValidationErrors(errors);
      return;
    }
    const postBody = {
      term: term,
      state: "active",
      semantictag: "finding++disorder",
      acceptability: "synonyms",
      returnlimit: "-1",
      refsetid: null,
      parentid: null,
      excludeparentconcept: false,
      fullconcept: true,
    };

    try {
      if (investigationName.length > 2) {
        const response = await axios.post(
          `${REACT_APP_BACKEND_ROUTE_URL}snomed/search/`,
          postBody,
          { headers: headers }
        );
        if (response) {
          let updatedFound = {},
            updatedSelectedOptiondetsils = {},
            foundData = false;
          console.log("Snomed Response", response);
          if (
            response.data.status === "Success" &&
            response.data.data.length > 0
          ) {
            foundData = response.data.data.find((d) => d.term === term);
            console.log("Found Data", foundData);
            if (foundData) {
              updatedFound = {
                ...foundData,
                found: true,
                id: foundData.conceptId,
              };
            } else {
              updatedSelectedOptiondetsils = {
                hierarchy: null,
                isPreferredTerm: null,
                conceptState: null,
                conceptFsn: term,
                definitionStatus: null,
                conceptId: null,
                languageCode: "en",
                typeId: null,
                term: term,
                caseSignificanceId: "CASE_INSENSITIVE",
                id: null,
                effectiveTime: null,
                activeStatus: null,
                moduleId: null,
              };
            }
          } else {
            updatedSelectedOptiondetsils = {
              hierarchy: null,
              isPreferredTerm: null,
              conceptState: null,
              conceptFsn: term,
              definitionStatus: null,
              conceptId: null,
              languageCode: "en",
              typeId: null,
              term: term,
              caseSignificanceId: "CASE_INSENSITIVE",
              id: null,
              effectiveTime: null,
              activeStatus: null,
              moduleId: null,
            };
          }

          setAutoCompleteData([]);
          setFilteredOptions([]);
          setSuggestApiCalled(false);
          setDebounceFunctionCalled(true);
          if (foundData) {
            handleInputChange("term", updatedFound);
          } else {
            handleInputChange("term", updatedSelectedOptiondetsils);
          }
        }
      }
    } catch (err) {
      console.log(err);
      toast.error(err);
    }
  };

  const handleConditionTyped = (value) => {
    setInvestigationName(value);

    // if (debounceTimeout.current) {
    //   clearTimeout(debounceTimeout.current); // Clear previous timeout
    // }

    // debounceTimeout.current = setTimeout(() => {
    //   handleAddOption(value); // Only triggers after user stops typing
    // }, 500); // Adjust delay as needed
  };

  const handleSuggestionClick = (value) => {
    setInvestigationName(value);
    setSuggestionClicked(true);

    // setKeepSuggestionsVisibleAfterClick(true); // allow options to remain visible
  };

  console.log("Tests and Investigations data", investigations);
  return (
    <ThemeProvider theme={theme}>
      {activeSection && (
        <Grid item xs={12}>
          <Suggestions
            sectionName={sectionName}
            suggestions={suggestions}
            activeSection={activeSection}
            aiSuggestionToggle={aiSuggestionToggle}
            ruleSuggestionToggle={ruleSuggestionToggle}
            handleCrossButtonClick={memoizedHandleCrossButtonClick}
            handleSuggestionClick={handleSuggestionClick}
          />
        </Grid>
      )}
      {activeSection && (
        <Card
          sx={{
            borderRadius: "10px",
            boxShadow: "0 4px 4px 0 #C7C7CC inset",
            backgroundColor: "#FFF",
          }}
        >
          <Grid item xs={12} sm={12} md={12}>
            <Grid
              container
              spacing={2}
              sx={{
                marginTop: "5px",
                padding: "10px",
              }}
            >
              <Grid item xs={12} sm={12} md={6}>
                <StyledTextField
                  value={
                    // newInvestigation.term !== ""
                    //   ? newInvestigation.term
                    //   :
                    investigationName
                  }
                  onChange={(e) => {
                    // if (!newInvestigation.term) {
                    //   setInvestigationName(e.target.value);
                    // }
                    handleConditionTyped(e.target.value);
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      const shouldClear =
                        !mouseDownOnOption && !suggestionClicked;

                      if (shouldClear) {
                        // setIsFocused(false);
                        setFilteredOptions([]);
                        setDebounceFunctionCalled(false);
                        setTermSelectedOrAdded(false);
                      }

                      setMouseDownOnOption(false);
                      setSuggestionClicked(false); // Reset
                    }, 100);
                  }}
                  onFocus={() => {
                    // setIsFocused(true);
                    if (investigationName.trim() !== "") {
                      debouncedFilterCall(investigationName.toLowerCase()); // re-trigger filtering
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <div
                        style={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <div style={{ marginRight: "5px" }}>
                          <Tooltip title="Clear">
                            <OutlinedIconFab
                              size="small"
                              variant="contained"
                              onClick={handleClearName}
                            >
                              <Icon name="clearTextField" size={32} />
                            </OutlinedIconFab>
                          </Tooltip>
                        </div>

                        <>
                          {isAiEnabled &&
                            (listeningOn === false ? (
                              <Tooltip title="Start Listening">
                                <OutlinedIconFab
                                  // onClick={startListening}
                                  onClick={() => {
                                    startRecording();
                                    setListeningOn(true);
                                  }}
                                  variant="contained"
                                  // color="defaultColor"
                                  size="small"
                                >
                                  <Icon name="micNotInUse" size={32} />
                                </OutlinedIconFab>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Stop Listening">
                                <OutlinedIconFab
                                  onClick={() => {
                                    // SpeechRecognition.stopListening();
                                    stopRecording("Tests/Investigations");
                                    setListeningOn(false);
                                  }}
                                  variant="outlined"
                                  // color="defaultColor"
                                  size="small"
                                >
                                  <Icon name="micInUse" size={32} />
                                </OutlinedIconFab>
                              </Tooltip>
                            ))}
                        </>
                      </div>
                    ),
                  }}
                  label="Test/Investigation Name"
                  fullWidth
                  error={!!validationErrors.term} // Highlight field in red if there's an error
                  helperText={validationErrors.term} // Show error message
                />
                {filteredOptions.length > 0 ? (
                  <div
                    style={{
                      backgroundColor: "#f5f5f5",
                      borderRadius: "10px",
                      padding: "20px",
                      maxHeight: "200px", // Set your desired maximum height here
                      overflowY: "auto", // Add vertical scrolling
                    }}
                  >
                    {/* Display filtered medicines */}
                    {suggestApiCalled && (
                      <div style={{ color: "red" }}>
                        No Exact Matches found ! You can search for the
                        following Suggestions.
                      </div>
                    )}

                    <>
                      {filteredOptions.map((op) => (
                        <div key={op}>
                          {
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              {op.added ? (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <StyledButton
                                    color="success"
                                    sx={{
                                      textAlign: "left",
                                      justifyContent: "flex-start",
                                      width: "100%",
                                    }}
                                  >
                                    {op.term}
                                  </StyledButton>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "flex-end",
                                      flexDirection: "row",
                                      color: "green",
                                      fontStyle: "bold",
                                    }}
                                  >
                                    <span>&#10003;</span>
                                    <span>(Added)</span>
                                  </div>
                                </div>
                              ) : (
                                <StyledButton
                                  color="grey"
                                  sx={{
                                    textAlign: "left",
                                    justifyContent: "flex-start",
                                    width: "100%",
                                  }}
                                  onMouseDown={() => setMouseDownOnOption(true)}
                                  onClick={() => {
                                    skipEffectForTest.current = true; // prevent debounce call
                                    testChosen.current = true;
                                    setInvestigationName(op.term);
                                    handleInputChange("term", op);
                                  }}
                                >
                                  {op.term}
                                </StyledButton>
                              )}
                            </div>
                          }
                        </div>
                      ))}
                    </>
                  </div>
                ) : filteredOptions.length == 0 &&
                  debounceFunctionCalled &&
                  !termSelectedOrAdded ? (
                  <div
                    style={{
                      backgroundColor: "#f5f5f5",
                      borderRadius: "10px",
                      padding: "20px",
                      maxHeight: "300px", // Set your desired maximum height here
                      overflowY: "auto", // Add vertical scrolling
                    }}
                  >
                    <div style={{ color: "red" }}>No Options Found!</div>
                  </div>
                ) : null}
              </Grid>

              <Grid item xs={12} sm={7} md={6}>
                <StyledTextField
                  value={bodyPartName}
                  onChange={(e) => {
                    setBodyPartName(e.target.value);
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      const shouldClear = !mouseDownOnBodyPartOption;

                      if (shouldClear) {
                        // setIsFocused(false);
                        setFilteredBodyPartsOptions([]);
                        setDebounceFunctionCalledForBodyParts(false);
                        // setTermSelectedOrAdded(false);
                      }

                      setMouseDownOnBodyPartOption(false);
                    }, 100);
                  }}
                  onFocus={() => {
                    // setIsFocused(true);
                    if (bodyPartName.trim() !== "") {
                      debouncedFilterCallForBodyParts(
                        bodyPartName.toLowerCase()
                      ); // re-trigger filtering
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <div
                        style={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <div style={{ marginRight: "5px" }}>
                          <Tooltip title="Clear">
                            <OutlinedIconFab
                              size="small"
                              variant="contained"
                              onClick={handleClearBodyPartName}
                            >
                              <Icon name="clearTextField" size={32} />
                            </OutlinedIconFab>
                          </Tooltip>
                        </div>

                        <>
                          {/* {listeningOn === false ? (
                          <Tooltip title="Start Listening">
                            <OutlinedIconFab
                              onClick={startListening}
                              variant="contained"
                              color="defaultColor"
                              size="small"
                            >
                              <MicOffIcon />
                            </OutlinedIconFab>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Stop Listening">
                            <OutlinedIconFab
                              onClick={() => {
                                SpeechRecognition.stopListening();
                                setListeningOn(false);
                              }}
                              variant="outlined"
                              color="defaultColor"
                              size="small"
                            >
                              <KeyboardVoiceIcon />
                            </OutlinedIconFab>
                          </Tooltip>
                        )} */}
                        </>
                      </div>
                    ),
                  }}
                  label="Anatomical Region"
                  fullWidth
                  error={!!validationErrors.body_part} // Highlight field in red if there's an error
                  helperText={validationErrors.body_part} // Show error message
                />
                {filteredBodyPartsOptions.length > 0 ? (
                  <div
                    style={{
                      backgroundColor: "#f5f5f5",
                      borderRadius: "10px",
                      padding: "20px",
                      maxHeight: "200px", // Set your desired maximum height here
                      overflowY: "auto", // Add vertical scrolling
                    }}
                  >
                    {/* Display filtered medicines */}
                    {suggestApiCalled && (
                      <div style={{ color: "red" }}>
                        No Exact Matches found ! You can search for the
                        following Suggestions.
                      </div>
                    )}

                    <>
                      {filteredBodyPartsOptions.map((op) => (
                        <div key={op}>
                          {
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              {op.added ? (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <StyledButton
                                    color="success"
                                    sx={{
                                      textAlign: "left",
                                      justifyContent: "flex-start",
                                      width: "100%",
                                    }}
                                  >
                                    {op.term}
                                  </StyledButton>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "flex-end",
                                      flexDirection: "row",
                                      color: "green",
                                      fontStyle: "bold",
                                    }}
                                  >
                                    <span>&#10003;</span>
                                    <span>(Added)</span>
                                  </div>
                                </div>
                              ) : (
                                <StyledButton
                                  color="grey"
                                  sx={{
                                    textAlign: "left",
                                    justifyContent: "flex-start",
                                    width: "100%",
                                  }}
                                  onClick={() => {
                                    skipEffectForBodyPart.current = true; // prevent debounce call
                                    bodyPartChosen.current = true;
                                    setBodyPartName(op.term);
                                    handleInputChange("body_part", op);
                                  }}
                                  onMouseDown={() =>
                                    setMouseDownOnBodyPartOption(true)
                                  }
                                >
                                  {op.term}
                                </StyledButton>
                              )}
                            </div>
                          }
                        </div>
                      ))}
                    </>
                  </div>
                ) : filteredBodyPartsOptions.length == 0 &&
                  debounceFunctionCalledForBodyParts &&
                  !bodyPartSelectedOrAdded ? (
                  <div
                    style={{
                      backgroundColor: "#f5f5f5",
                      borderRadius: "10px",
                      padding: "20px",
                      maxHeight: "300px", // Set your desired maximum height here
                      overflowY: "auto", // Add vertical scrolling
                    }}
                  >
                    <div style={{ color: "red" }}>No Options Found!</div>
                  </div>
                ) : null}
              </Grid>

              <Grid item xs={12} sm={5} md={3}>
                <StyledTextField
                  value={lateralityName}
                  onChange={(e) => {
                    setLateralityName(e.target.value);
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      const shouldClear = !mouseDownOnLateralityOption;

                      if (shouldClear) {
                        // setIsFocused(false);
                        setFilteredLateralityOptions([]);
                        setDebounceFunctionCalledForLaterality(false);
                        // setTermSelectedOrAdded(false);
                      }

                      setMouseDownOnLateralityOption(false);
                    }, 100);
                  }}
                  onFocus={() => {
                    // setIsFocused(true);
                    if (lateralityName.trim() !== "") {
                      debouncedFilterCallForLaterality(
                        lateralityName.toLowerCase()
                      ); // re-trigger filtering
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <div
                        style={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <div style={{ marginRight: "5px" }}>
                          <Tooltip title="Clear">
                            <OutlinedIconFab
                              size="small"
                              variant="contained"
                              onClick={handleClearLateralityName}
                            >
                              <Icon name="clearTextField" size={32} />
                            </OutlinedIconFab>
                          </Tooltip>
                        </div>

                        <>
                          {/* {listeningOn === false ? (
                          <Tooltip title="Start Listening">
                            <OutlinedIconFab
                              onClick={startListening}
                              variant="contained"
                              color="defaultColor"
                              size="small"
                            >
                              <MicOffIcon />
                            </OutlinedIconFab>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Stop Listening">
                            <OutlinedIconFab
                              onClick={() => {
                                SpeechRecognition.stopListening();
                                setListeningOn(false);
                              }}
                              variant="outlined"
                              color="defaultColor"
                              size="small"
                            >
                              <KeyboardVoiceIcon />
                            </OutlinedIconFab>
                          </Tooltip>
                        )} */}
                        </>
                      </div>
                    ),
                  }}
                  label="Laterality"
                  fullWidth
                  error={!!validationErrors.laterality} // Highlight field in red if there's an error
                  helperText={validationErrors.laterality} // Show error message
                />
                {filteredLateralityOptions.length > 0 ? (
                  <div
                    style={{
                      backgroundColor: "#f5f5f5",
                      borderRadius: "10px",
                      padding: "20px",
                      maxHeight: "200px", // Set your desired maximum height here
                      overflowY: "auto", // Add vertical scrolling
                    }}
                  >
                    {/* Display filtered medicines */}
                    {suggestApiCalled && (
                      <div style={{ color: "red" }}>
                        No Exact Matches found ! You can search for the
                        following Suggestions.
                      </div>
                    )}

                    <>
                      {filteredLateralityOptions.map((op) => (
                        <div key={op}>
                          {
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              {op.added ? (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <StyledButton
                                    color="success"
                                    sx={{
                                      textAlign: "left",
                                      justifyContent: "flex-start",
                                      width: "100%",
                                    }}
                                  >
                                    {op.term}
                                  </StyledButton>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "flex-end",
                                      flexDirection: "row",
                                      color: "green",
                                      fontStyle: "bold",
                                    }}
                                  >
                                    <span>&#10003;</span>
                                    <span>(Added)</span>
                                  </div>
                                </div>
                              ) : (
                                <StyledButton
                                  color="grey"
                                  sx={{
                                    textAlign: "left",
                                    justifyContent: "flex-start",
                                    width: "100%",
                                  }}
                                  onClick={() => {
                                    skipEffectForLaterality.current = true; // prevent debounce call
                                    lateralityChosen.current = true;
                                    setLateralityName(op.term);
                                    handleInputChange("laterality", op);
                                  }}
                                  onMouseDown={() =>
                                    setMouseDownOnLateralityOption(true)
                                  }
                                >
                                  {op.term}
                                </StyledButton>
                              )}
                            </div>
                          }
                        </div>
                      ))}
                    </>
                  </div>
                ) : filteredLateralityOptions.length == 0 &&
                  debounceFunctionCalledForLaterality &&
                  !lateralitySelectedOrAdded ? (
                  <div
                    style={{
                      backgroundColor: "#f5f5f5",
                      borderRadius: "10px",
                      padding: "20px",
                      maxHeight: "300px", // Set your desired maximum height here
                      overflowY: "auto", // Add vertical scrolling
                    }}
                  >
                    <div style={{ color: "red" }}>No Options Found!</div>
                  </div>
                ) : null}
              </Grid>

              <Grid item xs={12} sm={5} md={3}>
                <StyledTextField
                  value={positionName}
                  onChange={(e) => {
                    setPositionName(e.target.value);
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      const shouldClear = !mouseDownOnPositionOption;

                      if (shouldClear) {
                        // setIsFocused(false);
                        setFilteredPositionOptions([]);
                        setDebounceFunctionCalledForPosition(false);
                        // setTermSelectedOrAdded(false);
                      }

                      setMouseDownOnPositionOption(false);
                    }, 100);
                  }}
                  onFocus={() => {
                    // setIsFocused(true);
                    if (positionName.trim() !== "") {
                      debouncedFilterCallForPosition(
                        positionName.toLowerCase()
                      ); // re-trigger filtering
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <div
                        style={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <div style={{ marginRight: "5px" }}>
                          <Tooltip title="Clear">
                            <OutlinedIconFab
                              size="small"
                              variant="contained"
                              onClick={handleClearPositionName}
                            >
                              <Icon name="clearTextField" size={32} />
                            </OutlinedIconFab>
                          </Tooltip>
                        </div>

                        <>
                          {/* {listeningOn === false ? (
                          <Tooltip title="Start Listening">
                            <OutlinedIconFab
                              onClick={startListening}
                              variant="contained"
                              color="defaultColor"
                              size="small"
                            >
                              <MicOffIcon />
                            </OutlinedIconFab>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Stop Listening">
                            <OutlinedIconFab
                              onClick={() => {
                                SpeechRecognition.stopListening();
                                setListeningOn(false);
                              }}
                              variant="outlined"
                              color="defaultColor"
                              size="small"
                            >
                              <KeyboardVoiceIcon />
                            </OutlinedIconFab>
                          </Tooltip>
                        )} */}
                        </>
                      </div>
                    ),
                  }}
                  label="Patient Position"
                  fullWidth
                  error={!!validationErrors.position} // Highlight field in red if there's an error
                  helperText={validationErrors.position} // Show error message
                />
                {filteredPositionOptions.length > 0 ? (
                  <div
                    style={{
                      backgroundColor: "#f5f5f5",
                      borderRadius: "10px",
                      padding: "20px",
                      maxHeight: "200px", // Set your desired maximum height here
                      overflowY: "auto", // Add vertical scrolling
                    }}
                  >
                    {/* Display filtered medicines */}
                    {suggestApiCalled && (
                      <div style={{ color: "red" }}>
                        No Exact Matches found ! You can search for the
                        following Suggestions.
                      </div>
                    )}

                    <>
                      {filteredPositionOptions.map((op) => (
                        <div key={op}>
                          {
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              {op.added ? (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <StyledButton
                                    color="success"
                                    sx={{
                                      textAlign: "left",
                                      justifyContent: "flex-start",
                                      width: "100%",
                                    }}
                                  >
                                    {op.term}
                                  </StyledButton>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "flex-end",
                                      flexDirection: "row",
                                      color: "green",
                                      fontStyle: "bold",
                                    }}
                                  >
                                    <span>&#10003;</span>
                                    <span>(Added)</span>
                                  </div>
                                </div>
                              ) : (
                                <StyledButton
                                  color="grey"
                                  sx={{
                                    textAlign: "left",
                                    justifyContent: "flex-start",
                                    width: "100%",
                                  }}
                                  onClick={() => {
                                    skipEffectForPosition.current = true; // prevent debounce call
                                    positionChosen.current = true;
                                    setPositionName(op.term);
                                    handleInputChange("position", op);
                                  }}
                                  onMouseDown={() =>
                                    setMouseDownOnPositionOption(true)
                                  }
                                >
                                  {op.term}
                                </StyledButton>
                              )}
                            </div>
                          }
                        </div>
                      ))}
                    </>
                  </div>
                ) : filteredPositionOptions.length == 0 &&
                  debounceFunctionCalledForPosition &&
                  !positionSelectedOrAdded ? (
                  <div
                    style={{
                      backgroundColor: "#f5f5f5",
                      borderRadius: "10px",
                      padding: "20px",
                      maxHeight: "300px", // Set your desired maximum height here
                      overflowY: "auto", // Add vertical scrolling
                    }}
                  >
                    <div style={{ color: "red" }}>No Options Found!</div>
                  </div>
                ) : null}
              </Grid>

              <Grid item xs={12} sm={7} md={6}>
                <StyledMultilineTextField
                  value={newInvestigation.clinical_note}
                  onChange={(e) =>
                    handleInputChange("clinical_note", e.target.value)
                  }
                  placeholder="Clinical Notes"
                  fullWidth
                />
              </Grid>

              <Grid item xs={2} sx={{ display: "flex", alignItems: "center" }}>
                <StyledButton
                  onClick={editingIndex != null ? handleUpdate : handleAdd}
                  variant="contained"
                  sx={{
                    fontSize: { xs: "0.8rem", sm: "1rem" },
                    width: "100%",
                  }}
                  bgColor="#0E3F66"
                >
                  {editingIndex != null ? "Update" : "Add"}
                </StyledButton>
              </Grid>
            </Grid>
          </Grid>
        </Card>
      )}
      {investigations && (
        <InvestigationsTable
          data={investigations}
          activeSection={activeSection}
          handleEditRow={handleEditRow}
          handleDeleteRow={handleDeleteRow}
        />
      )}
    </ThemeProvider>
  );
};

export default Investigations;
